import dayjs from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"

import { hasAnyPermission } from "shared/imports/permissionFunctions"

import type { CopilotChatsInterface, CopilotChatInterface } from "@/components/CopilotAnalyze/types"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

dayjs.extend(isSameOrAfter)

export function checkCoPilotAnalyzeConditions(): boolean {
    // Do they have the Copilot Permission?
    const hasCopilot = hasAnyPermission(DjangIO.app.models.PermissionType.copilot_analyze)

    // Are they also individually allowed to view the Federal/State Bill Profiles?
    const hasIndividualRegionPermissions = hasAnyPermission(DjangIO.app.models.PermissionType.legislative_profiles, {
        // all_regions is actually all _US_ regions, but predates the International product
        regions: DjangIO.app.models.SupportedRegion.all_regions.child_regions as unknown as number[],
        useCurrentRegions: true,
    })

    if (isFeatureEnabled("ff_copilot_without_region_permissions")) {
        return hasCopilot
    }

    return hasCopilot && hasIndividualRegionPermissions
}

/**
 * Split a collection of sessions into relative time periods.
 * Sessions are grouped into the following time periods:
 * - Today
 * - Yesterday
 * - This Week
 * - This Month
 * - Each month in the current year
 * - Each year before that
 *
 * @param sessions Collection of sessions, each with a last_active timestamp
 * @returns collection of sessions grouped into relative time periods
 */
export const splitSessions = (sessions: CopilotChatsInterface) => {
    if (!sessions || !Object.values(sessions).length) return {}

    const today = dayjs().startOf("day")
    const yesterday = dayjs().subtract(1, "day").startOf("day")
    const thisWeek = dayjs().startOf("week")
    const thisMonth = dayjs().startOf("month")
    const thisYear = dayjs().startOf("year")

    return Object.values(sessions)
        .sort((a, b) => new Date(b.last_active).getTime() - new Date(a.last_active).getTime())
        .reduce(
            (acc, session) => {
                const lastActive = dayjs(session.last_active)

                if (lastActive.isSameOrAfter(today)) {
                    acc.Today ? acc.Today.push(session) : (acc.Today = [session])
                } else if (lastActive.isSameOrAfter(yesterday)) {
                    acc.Yesterday ? acc.Yesterday.push(session) : (acc.Yesterday = [session])
                } else if (lastActive.isSameOrAfter(thisWeek)) {
                    acc["This Week"] ? acc["This Week"].push(session) : (acc["This Week"] = [session])
                } else if (lastActive.isSameOrAfter(thisMonth)) {
                    acc["This Month"] ? acc["This Month"].push(session) : (acc["This Month"] = [session])
                } else if (lastActive.isSameOrAfter(thisYear)) {
                    const month = lastActive.format("MMMM")
                    acc[month] ? acc[month].push(session) : (acc[month] = [session])
                } else if (lastActive.isBefore(thisYear)) {
                    const year = lastActive.format("YYYY")
                    // In JS, object keys are always strings... but numbers are always ordered first :upside-down-smiley:
                    // So we'll append whitespace to the year to ensure it's not considered a number.
                    acc[`${year} `] ? acc[`${year} `].push(session) : (acc[`${year} `] = [session])
                }

                return acc
            },
            {} as Record<string, CopilotChatInterface[]>,
        )
}

export const getHasSessions = (sessions) => {
    const splits = splitSessions(sessions)
    return Boolean(Object.values(splits).flat().length)
}
